if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.initReadMoreBtn();
        this.filterVariantOptions();
        this.productInitData();
      }

      connectedCallback() {
        
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        // Subscribe to variant change events to update wishlist icon
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        
        // Ensure wishlist icon is always visible on initial load
        setTimeout(() => {
          this.ensureWishlistIconVisible();
        }, 300);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      productInitData() {

        const productUrl = this.pendingRequestUrl || this.dataset.url;
        const variant = this.getSelectedVariant(document.querySelector('product-info'));

        if (!variant) return;

        const productHandle = productUrl.match(/products\/([^/?#]+)/);

        if (!productHandle) return;
        this.getProductData(productHandle[1]).then(productData => {
          this.updateRequiredPartsModalTrigger(variant.id);
          this.updatePartsAccordion(variant.id);
          this.updateAccordionSwiperSlider();
          this.showWishlistIcon(variant.id);
          const productMediaContainer = this.querySelector(`product-media`)
          if (!productMediaContainer) return;
          productMediaContainer.swapImages(productData, variant);
          productMediaContainer.initPhotoSwipeEvents();
          this.removeNoImageMediaGallery();
        });
        
      }

      removeNoImageMediaGallery() {
        const container = document.querySelector('product-media');
        if(!container) return;
        const noImages = container.querySelectorAll('img[alt="no-image-media-gallery"]');
        if (noImages.length < 1) return;

        const mainGalleryImages = container.querySelectorAll('.product-media .product-media__images .swiper-slide img:not([alt="no-image-media-gallery"])');

          if (mainGalleryImages.length > 0) {
            noImages.forEach((image) => {
              image.closest('.swiper-slide').remove();
            });
          }
        container.initSlider();
        container.thumbnails.reinitSlider()
      }

      showWishlistIcon(variantId) {
        this.querySelectorAll('wishlist-icon').forEach(wishlistIcon => {
          wishlistIcon.classList.remove('active_variant');
        });
        
        // Find and show the matching wishlist icon
        const matchingIcon = this.querySelector(`wishlist-icon[data-variant-id="${variantId}"]`);
        if (matchingIcon) {
          matchingIcon.classList.add('active_variant');
        } else {
          // Fallback: if no matching icon found, show the first one
          const firstIcon = this.querySelector('wishlist-icon');
          if (firstIcon) {
            firstIcon.classList.add('active_variant');
          }
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.breadCrumbUrl = this.buildBreadCrumbUrlWithParams(productUrl, selectedOptionValues);
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
        
        // Comprehensive fallback: Update wishlist icon after any option value change
        setTimeout(() => {
          const currentVariant = this.getSelectedVariant(this);
          if (currentVariant && currentVariant.id) {
            this.showWishlistIcon(currentVariant.id);
          }
        }, 150);
      }

      handleVariantChange({ data: { variant } }) {
        if (variant && variant.id) {
          this.showWishlistIcon(variant.id);
        }
      }

      ensureWishlistIconVisible() {
        // Check if any wishlist icon is currently visible
        const activeIcon = this.querySelector('wishlist-icon.active_variant');
        if (!activeIcon) {
          // No active icon, try to get current variant and show its icon
          const currentVariant = this.getSelectedVariant(this);
          if (currentVariant && currentVariant.id) {
            this.showWishlistIcon(currentVariant.id);
          } else {
            // Fallback: show the first wishlist icon
            const firstIcon = this.querySelector('wishlist-icon');
            if (firstIcon) {
              firstIcon.classList.add('active_variant');
            }
          }
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);


          const productHandle = productUrl.match(/products\/([^/?#]+)/);
          if (productHandle) {
            this.getProductData(productHandle[1]).then(productData => {
              const relatedProducts = this.relatedProducts;
              if (relatedProducts) {
                const relatedUrl = relatedProducts.dataset.url;
                const newProductUrl = relatedUrl.replace(/product_id=([^&]*)/, `product_id=${productData.id}`);
                relatedProducts.dataset.url = newProductUrl;
                relatedProducts.updateUrlAndReload(newProductUrl);
              }
            });
          }

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('product-info'),
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {

        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.warn('Fetch aborted by user');
            } else {
              console.error(error);
            }
          }).finally(() => {
            this.filterVariantOptions();
            const popupModalElement = document.querySelectorAll('popup-modal');
            popupModalElement.forEach(popupModal => {
                popupModal.initTriggers();
            });
            const homeViewerElement = document.querySelector('home-viewer');
            if (homeViewerElement) homeViewerElement.listenToViewerOpen();
            const viewer3dElement = document.querySelector('viewer-3d');
            if (viewer3dElement) viewer3dElement.listenToViewerOpen();

            if (typeof PriceSpider !== 'undefined')  PriceSpider.rebind();


            this.productInitData();
            this.wishlistIconInit();

            if (!this.breadCrumbUrl) return;
            this.updateBreadCrumb(this.breadCrumbUrl);
          });
      }

      triggerSelectedVariant() {
        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) return;

        const allFieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
        const firstFieldsetWithoutSelection = allFieldsets.find(fieldset => {
          return !fieldset.querySelector('input[type="radio"]:checked');
        });
        if (!firstFieldsetWithoutSelection) return;

        const radio = firstFieldsetWithoutSelection.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      filterVariantOptions() {
        const productInfoNode = document.querySelector('product-info');
        if (!productInfoNode) return;

        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) return;

        const firstFieldset = variantSelects.querySelector('fieldset');
        if (!firstFieldset) return;

        const checkedInput = firstFieldset?.querySelector('input[type="radio"]:checked');
        const checkedValue = checkedInput?.value;
        if (!checkedValue) return;

        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));

        const countryIsoCode = window.countryIsoCode || 'US';

        fieldsets.forEach((fieldset, index) => {
          const inputRadios = fieldset.querySelectorAll('input[type="radio"]');

          inputRadios.forEach((radio) => {
            const radioVariantAvailableIn = radio.dataset.variantAvailableIn;
            const radioVariantTitle = radio.dataset.productVariantTitle;
            const radioProductTitle = radio.dataset.productTitle;

            const isNotAvailableInCountry = radioVariantAvailableIn && !radioVariantAvailableIn.includes(countryIsoCode);
            const hasMissingTitles = !radioVariantTitle && !radioProductTitle;

            if (isNotAvailableInCountry || hasMissingTitles) {
              radio.closest('div.swatch').remove();
            }
          });

          const hasValidOptions = fieldset.querySelectorAll('input[type="radio"]').length > 0;
          if (!hasValidOptions) {
            fieldset.remove();
          }
        });

        const parent = this.querySelector('fieldset[data-swatch="true"]');

        if (parent) {
          const isProduct = true;
          window.theme.swatchSorter.sortAndSelectFirstSwatch(parent, isProduct);
        }else {
          this.triggerSelectedVariant();
        }
        
      }

      wishlistIconInit () {
        const wishlistIcon = this.querySelector('wishlist-icon');
        if (!wishlistIcon) return;
        let selector = "product-info";
        if(window._swat){
            window._swat.initializeActionButtons(selector);
        }else{
            window.SwymCallbacks = window.SwymCallbacks || [];
            window.SwymCallbacks.push(function(){
                window._swat.initializeActionButtons(selector);
            });
        }
      }

      updateBreadCrumb(url) {
        fetch(url, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          document.querySelector('.breadcrumbs-section').innerHTML = html.querySelector('.breadcrumbs-section').innerHTML;

          HTMLUpdateUtility.viewTransition(
            document.querySelector('.breadcrumbs-section'),
            html.querySelector('.breadcrumbs-section'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.warn('Abort Fetch');
          } else {
            console.error('Error update breadcrumb:', error);
          }
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getCurrentProductVariants(productInfoNode) {
        const variantDataElement = productInfoNode.querySelector('variant-selects [data-product-variant]');
        if (!variantDataElement?.innerHTML) return null;
        try {
          return JSON.parse(variantDataElement.innerHTML);
        } catch (error) {
          console.error('Error parsing variant data:', error);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        // param to set a product custom view
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      buildBreadCrumbUrlWithParams(url, optionValues) {
        const params = [];
        const breadCrumbSection = document.querySelector('.breadcrumbs-section');

        if (!breadCrumbSection) return;

        params.push(`section_id=${breadCrumbSection.id}`);
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');

        const parent = variantSelects.querySelector('fieldset')

        if (parent) {
          const isProduct = true;
          window.theme.swatchSorter.sortAndSelectFirstSwatch(parent, isProduct);
        }

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateOptionValues(html);
          this.productInitData();

          if (!variant) {
            this.setUnavailable();
            return;
          }
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.updateRequiredPartsModalTrigger(variant.id);
          this.updatePartsAccordion(variant.id);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          // Ensure wishlist icon is updated for this variant (fallback for size-only changes)
          this.showWishlistIcon(variant.id);
          
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateRequiredPartsModalTrigger(variantId) {
        const requiredPartsMessages = this.querySelectorAll('[data-required-parts-modal-message]');
        requiredPartsMessages?.forEach(message => {
            message.dataset.requiredPartsModalMessage === String(variantId) ? 
                message.classList.remove('hidden') : 
                message.classList.add('hidden');
        });
      }

      updatePartsAccordion(variantId) {
        const accordions = this.querySelectorAll('[data-product-parts]');
        accordions?.forEach(message => {
          message.dataset.productParts === String(variantId) ? 
              message.classList.remove('hidden') : 
              message.classList.add('hidden');
        });
      }

      updateAccordionSwiperSlider() {
        this.querySelectorAll('component-accordion').forEach(accordion => {
          const accordionSwiper = accordion.querySelectorAll('swiper-slider');
          if (accordionSwiper.length < 1) return;
          accordionSwiper.forEach(slider => {
            if(!slider) return
            slider.reinitSlider();
          });

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      async getProductData(productHandle) {
          try {
              const product = await window.theme.product.loadProduct(productHandle);
              return product;
          } catch (error) {
              console.error('Error to get product info:', error);
          }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'product-recommendations'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      initReadMoreBtn() {
        this.querySelector('[data-read-more-button]')?.addEventListener('click', this.scrollToDescritpion.bind(this));
      }

      scrollToDescritpion() {
          const overviewAccordion = document.querySelector('[data-overview-accordion]');
          if (!overviewAccordion) return;

          const accordionTrigger = overviewAccordion.querySelector('[data-accordion-trigger]');
          if (!accordionTrigger) return;

          const offset = 100;
          const elementTop = accordionTrigger.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
              top: elementTop - offset,
              behavior: 'smooth'
          });

          if (!overviewAccordion.isOpen) {
              accordionTrigger.click();
          }
      }
    }
  );
}
